import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'
import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import { FaqContentType } from '@lib/data/faq'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { msgT } from '@lib/utils/i18n'
import date from '@lib/utils/date.utils'
import { defineMessage, Trans } from '@lingui/macro'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import Link from 'next/link'

const SpringBreakCodingCampSeo = formatSeoConfig({
  title: `Online Spring Break Coding Camp for Kids & Teens ${date().year()} | Codingal`,
  description: `Join Codingal's live online Spring Break Coding Camp for kids and teens ages 7-17. Explore Scratch, Python, Roblox, web development, app development, and AI through project-based classes during spring break.`,
  url: 'https://www.codingal.com/kids-teens-coding-camps/spring-break/',
  metaImage: 'https://www.codingal.com/images/coding-camp/spring-og.jpg',
})

const couponCode = 'SPRING50'
const year = date().year()

export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is Codingal's Spring Break Coding Camp ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal&apos;s Spring Break Coding Camp {year} is a live online camp
        for kids and teens where learners use their school break to build games,
        apps, and creative tech projects with expert guidance.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal's Spring Break Coding Camp ${year} is a live online camp for kids and teens where learners use their school break to build games, apps, and creative tech projects with expert guidance.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which age groups can attend the spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        The camp is designed for learners ages 7-17. Tracks are selected by age
        and experience level so elementary, middle, and high school students can
        learn at the right pace.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The camp is designed for learners ages 7-17. Tracks are selected by age and experience level so elementary, middle, and high school students can learn at the right pace.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is prior coding experience required to join the spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        No. Beginners can start with block-based programming or coding
        fundamentals, while experienced learners can choose more advanced
        options in Python, AI, Roblox, web development, or app development.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No. Beginners can start with block-based programming or coding fundamentals, while experienced learners can choose more advanced options in Python, AI, Roblox, web development, or app development.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What coding subjects can my child explore during spring break?`,
    }),
    acceptedAnswerText: (
      <>
        Learners can explore Scratch, Python, Roblox Studio, Lua, HTML, CSS,
        JavaScript, app development, and data science depending on the track
        they choose for spring break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners can explore Scratch, Python, Roblox Studio, Lua, HTML, CSS, JavaScript, app development, and data science depending on the track they choose for spring break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are spring break coding classes scheduled?`,
    }),
    acceptedAnswerText: (
      <>
        Spring break camps are designed for school holiday schedules. Families
        can pick the course that fits their child&apos;s interests, and the live
        sessions are structured as a short, focused program that keeps momentum
        high during the break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Spring break camps are designed for school holiday schedules. Families can pick the course that fits their child's interests, and the live sessions are structured as a short, focused program that keeps momentum high during the break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are the spring break coding camps online?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. All spring break coding camp sessions are hosted live online, so
        learners can join from home while still collaborating with instructors
        and peers in real time.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. All spring break coding camp sessions are hosted live online, so learners can join from home while still collaborating with instructors and peers in real time.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What projects will my child build during the spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Students build age-appropriate projects such as Scratch games, Python
        mini-apps, Roblox adventures, AI-powered experiments, and responsive
        websites. Every project is designed to be portfolio friendly and fun to
        share after camp.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Students build age-appropriate projects such as Scratch games, Python mini-apps, Roblox adventures, AI-powered experiments, and responsive websites. Every project is designed to be portfolio friendly and fun to share after camp.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What does my child need to join the spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners need a laptop or computer with a stable internet connection.
        Codingal provides the learning environment and guidance needed for the
        selected program.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners need a laptop or computer with a stable internet connection. Codingal provides the learning environment and guidance needed for the selected program.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What makes a spring break coding camp useful for kids and teens?`,
    }),
    acceptedAnswerText: (
      <>
        A spring break coding camp gives students a short, focused learning
        experience without the pressure of the school week. It helps them build
        practical tech skills, stay creatively engaged, and complete real
        projects in just a few sessions.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `A spring break coding camp gives students a short, focused learning experience without the pressure of the school week. It helps them build practical tech skills, stay creatively engaged, and complete real projects in just a few sessions.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Will my child receive a certificate after completing the spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. Learners receive a certificate after completing their camp track,
        which helps them showcase their progress and celebrate the projects they
        built during spring break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Learners receive a certificate after completing their camp track, which helps them showcase their progress and celebrate the projects they built during spring break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I register for Codingal's spring break coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Choose the track that matches your child&apos;s age and interests,
        complete the online sign-up flow, and apply the coupon code shown on the
        page to secure a discounted seat.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Choose the track that matches your child's age and interests, complete the online sign-up flow, and apply the coupon code shown on the page to secure a discounted seat.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What support does Codingal provide beyond live spring break classes?`,
    }),
    acceptedAnswerText: (
      <>
        Families get a guided online learning experience, project-based
        instruction, access to expert teachers, and continued momentum into the
        next Codingal course or camp after spring break ends.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Families get a guided online learning experience, project-based instruction, access to expert teachers, and continued momentum into the next Codingal course or camp after spring break ends.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is Codingal's refund policy?`,
    }),
    acceptedAnswerText: (
      <>
        If the Spring Break Coding Camp does not meet your expectations, you can
        cancel and receive a full refund. Email{' '}
        <a
          href="mailto:support@codingal.com"
          target="_blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          support@codingal.com
        </a>{' '}
        to request a refund for any remaining classes.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `If the Spring Break Coding Camp does not meet your expectations, you can cancel and receive a full refund. Email support@codingal.com to request a refund for any remaining classes.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why should I trust Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal was founded by experienced edtech entrepreneurs, IIT alumni,
        and former leaders from Google and Amazon, with backing from investors
        like Y Combinator and Rebright Partners. Learn more about us{' '}
        <Link href="/about-us" prefetch={false}>
          <a className="text-blue-200 underline">here</a>
        </Link>
        .
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal was founded by experienced edtech entrepreneurs, IIT alumni, and former leaders from Google and Amazon, with backing from investors like Y Combinator and Rebright Partners. Learn more about us here.`,
    }),
  },
]

const SpringBreakCodingCampPage = () => {
  return (
    <>
      <NextSeo {...SpringBreakCodingCampSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText:
              typeof acceptedAnswerTextString === 'string'
                ? acceptedAnswerTextString
                : msgT(acceptedAnswerTextString),
            questionName:
              typeof questionName === 'string'
                ? questionName
                : msgT(questionName),
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        breadcrumbItems={[
          { name: 'Codingal', link: '/' },
          { name: 'Coding camps', link: '/kids-teens-coding-camps/' },
          {
            name: 'Spring break camp',
            link: '/kids-teens-coding-camps/spring-break/',
          },
        ]}
        title={
          <>
            <Trans>Spring Break Coding Camp for Kids &amp; Teens</Trans> {year}
          </>
        }
        description={
          <>
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                Turn spring break into a week of building, coding, and creative
                problem solving. Codingal&apos;s live online Spring Break Coding
                Camp helps kids and teens explore Scratch, Python, Roblox, web
                development, app development, and AI through guided hands-on
                projects.
              </Trans>
            </p>
            <p className="text-center lg:text-left pb-2 text-sm lg:text-base">
              <Trans>
                Designed for busy school-break schedules, our camp combines
                expert instruction, beginner-friendly onboarding, and
                portfolio-ready outcomes. Students stay engaged, build
                confidence, and finish spring break with skills they can carry
                into school and future tech programs.
              </Trans>
            </p>
          </>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={75}
        offerPercentage={50}
        imagePath="/images/coding-camp/spring-first-fold-image"
        imageAlt="Spring break coding camp for kids and teens by Codingal"
        backgroundColour="radial-gradient(circle at 12% 18%, rgba(255, 205, 96, 0.45) 0%, rgba(255, 205, 96, 0) 46%), radial-gradient(circle at 82% 12%, rgba(112, 218, 193, 0.38) 0%, rgba(112, 218, 193, 0) 48%), linear-gradient(135deg, #123a58 0%, #1d6a73 48%, #48a868 100%)"
        couponCodeTextBg="linear-gradient(135deg, #3d726c 0%, #35726f 100%)"
      />
      <Partners />
      <Courses
        couponCode={couponCode}
        title={
          <Trans>
            Hand-picked Spring Break Coding Camps tailored to your child
          </Trans>
        }
      />
      <VideoTestimonial
        title="What parents and students say about our spring break coding camp"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview isCampPage />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={
          <Trans>Exclusive benefits of our Spring Break Coding Camp</Trans>
        }
      />
      <MoneyBackGuarantee
        isCodingCampPage
        title="Why parents choose Codingal's spring break coding camp"
      />
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 18% 14%, rgba(255, 223, 140, 0.65) 0%, rgba(255, 223, 140, 0) 40%), radial-gradient(circle at 82% 78%, rgba(159, 232, 201, 0.55) 0%, rgba(159, 232, 201, 0) 48%), linear-gradient(140deg, #f3fff8 0%, #dff7ec 52%, #cff2e3 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                A spring break camp packed with{' '}
                <span className="yellow-underline">
                  creativity, coding, and confidence
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    Spring break is one of the best times for focused,
                    low-stress enrichment. Instead of losing academic momentum,
                    learners spend the week building games, apps, and digital
                    projects that feel fun from the very first session.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Our curriculum is structured around the search intent
                    families care about most: live online instruction,
                    beginner-friendly entry points, age-based learning paths,
                    and real outcomes students can show off after camp ends.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-activities"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 82%, rgba(255, 207, 110, 0.6) 0%, rgba(255, 207, 110, 0) 42%), radial-gradient(circle at 80% 18%, rgba(75, 190, 167, 0.45) 0%, rgba(75, 190, 167, 0) 50%), linear-gradient(140deg, #e5fff4 0%, #d7faee 54%, #c0f1de 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                STEM.org Accredited{' '}
                <span className="yellow-underline">Educational Experience</span>{' '}
                and Certificates
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    Codingal&apos;s spring break camps extend the same
                    STEM.org-accredited learning framework trusted across our
                    programs. That means kids and teens do more than stay busy
                    during the holiday. They build future-ready technical and
                    problem-solving skills through structured instruction.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Certificates help students celebrate progress, document
                    their project work, and share a meaningful spring break
                    achievement with teachers, schools, and family.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate"
            imageWidth={370}
            imageHeight={370}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 18% 16%, rgba(120, 220, 174, 0.52) 0%, rgba(120, 220, 174, 0) 40%), radial-gradient(circle at 82% 78%, rgba(255, 217, 125, 0.48) 0%, rgba(255, 217, 125, 0) 50%), linear-gradient(135deg, #f8fffb 0%, #e8fff4 55%, #dbf7eb 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Unlock your{' '}
                <span className="yellow-underline">
                  skills and earn coding certifications
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    Students learn by shipping real work. Younger coders create
                    interactive stories and games in Scratch. Older learners can
                    dive into Python logic, app workflows, front-end coding, AI
                    ideas, and Roblox creation during a short high-impact camp
                    format.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    That project-based structure makes spring break camps
                    especially SEO-relevant for families looking for “coding
                    camps near me” alternatives that are flexible, online, and
                    actually educational instead of passive screen time.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate-2"
            imageWidth={370}
            imageHeight={370}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 82%, rgba(255, 224, 156, 0.62) 0%, rgba(255, 224, 156, 0) 42%), radial-gradient(circle at 78% 28%, rgba(126, 212, 238, 0.46) 0%, rgba(126, 212, 238, 0) 52%), linear-gradient(145deg, #f5fff9 0%, #e0faf0 56%, #d1f3e8 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                The best spring break camp for{' '}
                <span className="yellow-underline">
                  kids to learn coding basics and fundamentals
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    Parents searching for the best spring break coding camp
                    usually want three things: practical skills, an engaging
                    format, and clear supervision from live teachers. This page
                    is built around that demand, and the camp experience is too.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Codingal&apos;s camp tracks teach logic, sequencing,
                    debugging, design thinking, and digital creativity in a way
                    that feels approachable for beginners and challenging for
                    motivated learners.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    By the end of spring break, students do not just watch
                    tutorials. They complete projects, practice problem solving,
                    and leave with stronger confidence around technology.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-student-learning"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 18% 18%, rgba(123, 210, 172, 0.5) 0%, rgba(123, 210, 172, 0) 38%), radial-gradient(circle at 80% 76%, rgba(255, 210, 112, 0.5) 0%, rgba(255, 210, 112, 0) 48%), linear-gradient(145deg, #effff8 0%, #ddf8ed 55%, #ccf1e3 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Our spring break courses are appropriate for{' '}
                <span className="yellow-underline">
                  high school, middle school,
                </span>{' '}
                and elementary school students
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    Search behavior for spring break camps is highly
                    age-specific, so this page speaks clearly to families with
                    elementary, middle, and high school learners. Students are
                    matched to tracks that fit their maturity, goals, and coding
                    background.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    That means younger learners can start with visual coding and
                    storytelling, while older students move into Python, data,
                    web, or app projects that feel more career-aligned and
                    portfolio ready.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-school-kids"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <Faq
        title="Got questions? Find answers in our FAQ section"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default SpringBreakCodingCampPage
